import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    ArticleModel,
    AttributionBudgetModelDto,
    ModeleEnveloppeDto,
    MutationGetAttibutionBdgModelWithIdArgs,
    MutationGetListeArticleModelArgs,
    MutationGetListeEnveloppeModeleGeneriqueArgs,
    MutationSaveAttributionBudgetArgs,
    MutationSupprimerAttributionBudgetArgs,
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    SharedChequesAllocationDeleteAction,
    SharedChequesAllocationGetAction,
    SharedChequesAllocationLoadRelatedListAction,
    SharedChequesAllocationSaveAction
} from 'src/app/_shared/stores/shared-cheques-allocation/shared-cheques-allocation.action';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { Logger } from 'src/app/_core/tools/logger';
import { Observable } from 'rxjs';
import { allocationToXML } from 'src/app/board/subvention/stores/subvention-allocation/subvention-allocation.xml';

export class SharedChequesAllocationStateModel {

    allocation: {
        id: number,
        data: AttributionBudgetModelDto
    } = null;

    envelopesList: ModeleEnveloppeDto[];

    articlesList: ArticleModel[];
}

@Injectable()
@State<SharedChequesAllocationStateModel>({
    name: 'sharedChequesAllocation',
    defaults: new SharedChequesAllocationStateModel()
})
export class SharedChequesAllocationState {

    protected readonly logger = new Logger('AllocationState');

    constructor(private graphqlService: GraphqlService) {
    }

    @Selector()
    public static allocation(state: SharedChequesAllocationStateModel): AttributionBudgetModelDto {
        return state.allocation?.data;
    }

    @Selector()
    public static envelopesList(state: SharedChequesAllocationStateModel): ModeleEnveloppeDto[] {
        return state.envelopesList;
    }

    @Selector()
    public static articlesList(state: SharedChequesAllocationStateModel): ArticleModel[] {
        return state.articlesList;
    }

    @Action(SharedChequesAllocationGetAction)
    getAllocation(
        {getState, patchState}: StateContext<SharedChequesAllocationStateModel>,
        {allocationId, options}: SharedChequesAllocationGetAction
    ): Observable<any> {
        if (!allocationId) {
            patchState({allocation: null});
            return;
        }

        if (getState().allocation?.id === allocationId && !options.force) {
            this.logger.debug()('Get Allocation prevented, using existing');
            return;
        }

        return this.graphqlService.mutateOne<'GetAttibutionBDGModelWithId', MutationGetAttibutionBdgModelWithIdArgs>({
            mutation: gql`
                mutation GetAttibutionBDGModelWithId($contexte: ContextInfosInput, $idAtb: Float) {
                    GetAttibutionBDGModelWithId(contexte: $contexte idAtb: $idAtb) {
                        Id
                        IdActivite
                        Libelle
                        IdEnveloppe
                        Code
                        IsNominative
                        DateValeurDefaut
                        DateEvenement
                        IdAnneeRef
                        Description
                        IsCloture
                        IdDelegation
                        IsAttribBoule
                        IsMasquee
                        EnveloppeModel {
                            Id
                            Libelle
                        }
                        ArticleModele {
                            ID
                            Libelle
                            LibelleProduit
                        }
                        IdRglAcc
                        MoteurRegleAcc {
                            Id
                            LibelleRegle
                            IsRegleAvancee
                            ModeleRegleAcc {
                                Id
                                IsNominative
                                IsConjointHandicape
                                IsEnfantDirect
                                IsEnfantHandicape
                                IsEnfantACharge
                                AgeEnfantMin
                                AgeEnfantMax
                                IdTypeDateRefAge
                                ModeAccess
                                IdtypeRefAnc
                                # Type de date Saisie
                                DateRefJour
                                DateRefMois
                                DateRefJourAgeEnf
                                DateRefMoisAgeEnf
                                # Pour tous les ouvrants droits par ancienneté
                                RgModAccesAnMin
                                RgModAccesAnMax
                                # Variable selon critères
                                IdChamp
                                IdChampAD
                                ListeIdChampAD {
                                    decimal
                                }
                                ListeIdTypeBenef {
                                    decimal
                                }
                                ListeModeleRegleAccListeModele {
                                    RegleAccListeValModelDTO {
                                        IdValeurChamp
                                        AncienneteMinimum # Par ancienneté
                                        AncienneteMaximum # Par ancienneté
                                    }
                                }
                            }
                        }
                        IdRglSubQta
                        MoteurRegleSubQta {
                            Id
                            LibelleRegle
                            IsRegleAvancee
                            TypeRegle
                            ModeleRegleSubQta {
                                Id
                                IdTypeDateRefPond
                                DateRefPondJour
                                DateRefPondMois
                                # Variable selon critères
                                IdChamp
                                ListeModeleRegleSubQuotaValModele {
                                    RegleSubQuotaListeValModelDTO {
                                        Id
                                        ChpLstValId # Critère de type Liste
                                        De # Critère de type numérique
                                        A # Critère de type Numérique
                                        Od
                                        Conjoint
                                        Enfant
                                        Externe
                                        ValMin
                                        ValMax
                                    }
                                }
                                # Paramètres
                                RglModeleIdEnfDir
                                RglModeleIdEnfHan
                                RglModeleIdEnfAch
                                RglModeleIdAgeEnfMin
                                RglModeleIdAgeEnfMax
                                IsPondererTempsTravail
                                IsPondererMoisPresence
                                IsPondererMoisEntier
                            }
                        }
                        ListeClesRepartition {
                            CleRepartitionModelDTO {
                                SectionAnalytique {
                                    Id
                                }
                            }
                        }

                        DateDernierMAJ

                        # Exécution
                        AttribTgExec
                        DateExec
                    }
                }
            `,
            variables: {
                idAtb: allocationId
            }
        }).pipe(
            tap((GetAttibutionBDGModelWithId) => {
                patchState({
                        allocation: {
                            id: allocationId,
                            data: GetAttibutionBDGModelWithId
                        }
                    }
                );
            })
        );
    }

    @Action(SharedChequesAllocationLoadRelatedListAction)
    loadRelatedList({patchState}: StateContext<SharedChequesAllocationStateModel>, {activityId}: SharedChequesAllocationLoadRelatedListAction): Observable<any> {
        type mutations = 'GetListeEnveloppeModeleGenerique' | 'GetListeArticleModel';
        type args = MutationGetListeEnveloppeModeleGeneriqueArgs | MutationGetListeArticleModelArgs;

        return this.graphqlService.mutateMultiple<mutations, args>({
            mutation: gql`
                mutation GetListeEnveloppeModeleGenerique($contexte: ContextInfosInput, $critere: CriteresEnveloppeQuotaDTOInput, $criteres: CriteresArticleDTOInput) {
                    GetListeEnveloppeModeleGenerique(contexte: $contexte, critere: $critere) {
                        ModeleEnveloppeDTO {
                            Id
                            Libelle
                            IsNominatif
                        }
                    }
                    GetListeArticleModel(contexte: $contexte, criteres: $criteres) {
                        ArticleModel {
                            ID
                            Libelle
                            LibelleProduit
                        }
                    }
                }
            `,
            variables: {
                critere: {
                    IsEnveloppeForAttributionParam: true,
                    IsCloture: false
                },
                criteres: {
                    IdActivite: activityId,
                    IsCloture: false,
                    IsArticleChequierDynamique: false
                }
            }
        }).pipe(
            tap(({GetListeEnveloppeModeleGenerique, GetListeArticleModel}) =>
                patchState({
                    envelopesList: GetListeEnveloppeModeleGenerique?.ModeleEnveloppeDTO || [],
                    articlesList: GetListeArticleModel?.ArticleModel || [],
                })
            )
        );
    }

    @Action(SharedChequesAllocationSaveAction)
    saveAllocation(
        {getState, patchState, dispatch}: StateContext<SharedChequesAllocationStateModel>,
        {model}: SharedChequesAllocationSaveAction
    ): Observable<any> {
        return this.graphqlService.mutateOne<'SaveAttributionBudget', MutationSaveAttributionBudgetArgs>({
            mutation: gql`
                mutation SaveAttribution($contexte: ContextInfosInput, $rawXML: String) {
                    SaveAttributionBudget(contexte:$contexte,  rawXML: $rawXML)
                }
            `,
            variables: {
                rawXML: allocationToXML(model.allocation)
            }
        }).pipe(
            switchMap(allocationId => dispatch(new SharedChequesAllocationGetAction(allocationId, {force: true})))
        );
    }

    @Action(SharedChequesAllocationDeleteAction)
    deleteAllocation(
        {getState, patchState}: StateContext<SharedChequesAllocationStateModel>,
        {allocationId}: SharedChequesAllocationDeleteAction
    ): Observable<any> {
        return this.graphqlService.mutateOne<'SupprimerAttributionBudget', MutationSupprimerAttributionBudgetArgs>({
            mutation: gql`
                mutation SupprimerAttributionBudget($contexte: ContextInfosInput, $idAtb: Float) {
                    SupprimerAttributionBudget(contexte: $contexte, idAtb: $idAtb)
                }
            `,
            variables: {
                idAtb: allocationId
            },
            context: {
                [IGNORE_ALL_GRAPHQL_ERRORS]: true
            }
        });
    }

}
